/* THE FOLLOW-UP PIPELINE. The loop is only closed by an OUTCOME (SITE-RULES 11d).
 *
 * The panel this replaces was a cadence list: a name, a source, and "Overdue 21d". It
 * could not say what anybody had done about it or what came back, which means nothing
 * could ever learn which actions actually work.
 *
 * Every row carries why the person is here, what happened, the action owed and its
 * move it asks for, who owes it, when, WHAT CAME BACK, and the status.
 * And it takes the outcome in place, because a queue you have to leave to answer is a
 * queue that does not get answered.
 *
 * MEMBERS AND LEADS SHARE IT, because a follow-up is a follow-up whoever it is on.
 */
import { useState } from 'react';
import type { FollowUp } from '@acrostic/model';
import { useSource } from '../../data/SourceProvider.js';
import { NoneRow } from '../../components/Panel.js';
import { Field, Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { fmt } from '../../lib/fmt.js';
import { go } from '../../routes.js';

const STATUS_TONE: Record<string, string> = {
  overdue: 'risk', in_progress: 'warn', pending: '', completed: 'good', dismissed: '',
};

/* What actually comes back from a reach-out. A free-text box would give a field
   nothing can group on, which is how 351 real reasons ended up in a column nobody
   reported on. These are the answers, and there is a note for the rest of it. */
const OUTCOMES = [
  'Replied', 'No response', 'Call completed', 'Meeting scheduled',
  'Returned to class', 'Not ready, later', 'Asked us to stop',
];

export function PipelinePanel({ rows, onChanged }: {
  rows: FollowUp[]; onChanged: () => void;
}) {
  const [recording, setRecording] = useState<FollowUp | null>(null);
  const open = rows.slice(0, 5);

  return (
    <section className="panel">
      <header>
        <h2 className="t-heading-h3">Follow-up pipeline</h2>
        <span className="eyebrow t-label-xsmall">Action, response, outcome</span>
        <span className="spacer" />
        {rows.length > open.length && (
          <span className="t-caption muted">{rows.length - open.length} more</span>
        )}
      </header>

      {!rows.length ? <NoneRow>Nothing owed right now.</NoneRow> : (
        <table className="table fu">
          <thead>
            <tr className="t-label-small">
              <th>Why they are here</th><th>What to do</th><th>Due</th>
              <th>What came back</th><th />
            </tr>
          </thead>
          <tbody>
            {open.map((f) => (
              <tr key={f.id} style={{ cursor: 'default' }}>
                <td>
                  <div className="t-label-large">{f.why}</div>
                  <div className="t-caption muted">{f.happened}</div>
                </td>
                <td>
                  <div className="t-body-small">{f.action}</div>
                  <div className="t-caption muted">{f.owner}</div>
                </td>
                <td className="t-caption muted">{fmt.ago(f.due)}</td>
                <td>
                  {/* An open follow-up has no outcome, and saying so is the point of
                      the column: an empty cell reads as a missing feature. */}
                  {f.outcome
                    ? <>
                        <div className="t-body-small">{f.outcome}</div>
                        <div className="t-caption muted">{f.outcomeDetail}</div>
                      </>
                    : <span className={'badge ' + (STATUS_TONE[f.status] ?? '') + ' t-label-xsmall'}>
                        {f.status.replace('_', ' ')}
                      </span>}
                </td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="btn t-label-small" onClick={() => setRecording(f)}>
                    Record outcome
                  </button>
                  {(f.memberId || f.leadId) && (
                    <button
                      type="button" className="go t-label-medium" title="Open the record"
                      onClick={() => go((f.memberId ? '/members/' : '/leads/') + (f.memberId ?? f.leadId))}
                    >→</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {recording && (
        <OutcomeModal
          f={recording}
          onClose={() => setRecording(null)}
          onSaved={() => { setRecording(null); onChanged(); }}
        />
      )}
    </section>
  );
}

function OutcomeModal({ f, onClose, onSaved }: {
  f: FollowUp; onClose: () => void; onSaved: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [outcome, setOutcome] = useState(OUTCOMES[0]!);
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      title="What came back?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn primary t-label-medium" disabled={busy}
            onClick={() => {
              setBusy(true);
              void source.recordOutcome(f.id, outcome, detail.trim())
                .then(() => { toast('Outcome recorded', 'good'); onSaved(); })
                .catch((e: Error) => { toast(e.message, 'bad'); setBusy(false); });
            }}
          >Record it</button>
        </>
      }
    >
      <p className="t-body-small muted">{f.why} · {f.action}</p>
      <Field label="Outcome" hint="Without this, nothing can learn which actions bring people back.">
        <select className="field" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
          {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <Field label="In your words">
        <textarea className="field" rows={3} value={detail} autoFocus
          placeholder="What they actually said, and what happens next."
          onChange={(e) => setDetail(e.target.value)} />
      </Field>
    </Modal>
  );
}
